import type { CollectionId } from '@platewell/core';
import { useCollections, useConnectionStatus, useStore, type CollectionSummary } from '@platewell/query';
import { useState } from 'react';
import { DeleteCollectionDialog } from './delete-collection-dialog.js';
import { isCollectionNameTaken } from '../lib/collection-name.js';

/**
 * Renaming, adding and deleting the household's collections.
 *
 * Shared by the General settings tab and the standalone `/collections/manage` page, which
 * had grown two copies of the same screen — same state, same duplicate-name rules, same
 * delete dialog. The rules are the reason to keep one copy: a collection name is unique
 * per household, and a recipe always belongs to at least one collection, so what may be
 * renamed and what may be deleted is not obvious enough to re-derive twice.
 */
export function CollectionsManager() {
  const collections = useCollections();
  const { readOnly } = useConnectionStatus();
  const store = useStore();
  const [newCollection, setNewCollection] = useState('');
  const [adding, setAdding] = useState(false);
  const [renamingId, setRenamingId] = useState<CollectionId | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleting, setDeleting] = useState<CollectionSummary | null>(null);

  const trimmedNew = newCollection.trim();
  const isDuplicateCollection = isCollectionNameTaken(
    trimmedNew,
    collections.map((entry) => entry.name),
  );
  const canAddCollection = trimmedNew !== '' && !isDuplicateCollection;

  const trimmedRename = renameValue.trim();
  const isDuplicateRename =
    renamingId != null &&
    isCollectionNameTaken(
      trimmedRename,
      collections.filter((entry) => entry.id !== renamingId).map((entry) => entry.name),
    );
  const canSaveRename = trimmedRename !== '' && !isDuplicateRename;

  async function saveRename(id: CollectionId) {
    if (!canSaveRename) return;
    await store.renameCollection(id, trimmedRename);
    setRenamingId(null);
  }

  function addCollection() {
    if (!canAddCollection) return;
    void store.createCollection({ name: trimmedNew });
    closeAddForm();
  }
  function closeAddForm() {
    setAdding(false);
    setNewCollection('');
  }


  return (
    <>
      {collections.map((collection) => {
        const isRenaming = renamingId === collection.id;

        return (
          <div className="kv" key={collection.id}>
            {isRenaming ? (
              <div style={{ flex: 1, minWidth: 0 }}>
                <form
                  className="row wrap"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void saveRename(collection.id);
                  }}
                >
                  <input
                    className="input"
                    style={{ maxWidth: 260 }}
                    value={renameValue}
                    autoFocus
                    onChange={(event) => setRenameValue(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') setRenamingId(null);
                    }}
                  />
                  <button type="submit" className="btn primary" disabled={!canSaveRename}>
                    Save
                  </button>
                  <button type="button" className="btn ghost" onClick={() => setRenamingId(null)}>
                    Cancel
                  </button>
                </form>
                {isDuplicateRename && (
                  <p className="error-text" style={{ marginTop: 8 }}>
                    A collection named “{trimmedRename}” already exists.
                  </p>
                )}
              </div>
            ) : (
              <span>
                {collection.name}
                {collection.isSystem && <span className="hint"> · default</span>}
              </span>
            )}
            <span className="row" style={{ gap: 8, flexShrink: 0 }}>
              <span className="hint">
                {collection.recipeCount} {collection.recipeCount === 1 ? 'recipe' : 'recipes'}
              </span>
              {!readOnly && !isRenaming && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setRenamingId(collection.id);
                    setRenameValue(collection.name);
                  }}
                >
                  Rename
                </button>
              )}
              {!readOnly && !isRenaming && (
                // A disabled button swallows pointer events, so the tooltip that explains
                // why the default collection can't be deleted has to sit on a wrapper.
                <span
                  title={
                    collection.isSystem ? 'The default collection cannot be deleted.' : undefined
                  }
                >
                  <button
                    type="button"
                    className="btn danger"
                    disabled={collection.isSystem}
                    onClick={() => setDeleting(collection)}
                  >
                    Delete
                  </button>
                </span>
              )}
            </span>
          </div>
        );
      })}

      {!readOnly && !adding && (
        <div className="row" style={{ marginTop: 12 }}>
          <button type="button" className="btn" onClick={() => setAdding(true)}>
            Add collection
          </button>
        </div>
      )}

      {!readOnly && adding && (
        <div style={{ marginTop: 12 }}>
          <form
            className="row wrap"
            onSubmit={(event) => {
              event.preventDefault();
              addCollection();
            }}
          >
            <input
              className="input"
              style={{ maxWidth: 260 }}
              value={newCollection}
              autoFocus
              aria-label="Collection name"
              placeholder="Weeknight dinners"
              onChange={(event) => setNewCollection(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') closeAddForm();
              }}
            />
            <button type="submit" className="btn primary" disabled={!canAddCollection}>
              Add
            </button>
            <button type="button" className="btn ghost" onClick={closeAddForm}>
              Cancel
            </button>
          </form>
          {isDuplicateCollection && (
            <p className="error-text" style={{ marginTop: 8 }}>
              A collection named “{trimmedNew}” already exists.
            </p>
          )}
        </div>
      )}

      {deleting != null && (
        <DeleteCollectionDialog
          collection={deleting}
          targets={collections.filter((entry) => entry.id !== deleting.id)}
          onClose={() => setDeleting(null)}
          onDeleted={() => setDeleting(null)}
        />
      )}
    </>
  );
}
